import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import {
  useRecoilValue,
  useRecoilState,
  useRecoilValueLoadable
} from 'recoil';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Avatar,
  Table,
  notification,
  Input,
  Tag,
  Tooltip
} from 'antd';
import { Comment } from '@ant-design/compatible';
import { PageHeader } from '@ant-design/pro-layout';
import moment from 'moment';
import {
  PlusOutlined
} from '@ant-design/icons';
import Grocefy from '../../../assets/images/grocefyLogoAlone.png';
import {
  businessAtom,
  selectedSideAtom,
  refreshSidesAtom,
  selectedOptionAtom,
  stateAtom,
  profileAtom
} from '../../../atoms/Atoms';
import EditItemSide from './shared-components/EditItemSide';
import EditSideOption from './shared-components/EditSideOption';
import api from '../../../api/api';

function ItemSides() {
  const { Search } = Input;
  const currentState = useRecoilValue(stateAtom);
  const store = useRecoilValue(businessAtom);
  const selectedOption = useRecoilValue(selectedOptionAtom);
  const [selectedSide, setSelectedSide] = useRecoilState(selectedSideAtom);
  const [refreshSides, setRefreshSides] = useRecoilState(refreshSidesAtom);
  const [loading, setLoading] = useState(false);
  const [sides, setSides] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchText, setSearchText] = useState('');
  const { t } = useTranslation();

  const userProfile = useRecoilValueLoadable(profileAtom);
  const isAdmin =
    userProfile.contents && userProfile?.contents?.roles?.includes('SuperAdmin');
  const isCustomerService =
    isAdmin || (userProfile?.contents && (userProfile?.contents?.roles?.includes('CustomerServiceAdmin') || userProfile?.contents?.roles?.includes('CustomerService')));
  const isStoreManager =
    isAdmin || isCustomerService || (userProfile?.contents && (userProfile?.contents?.roles?.includes('StoreManager') || userProfile?.contents?.roles?.includes('StoreAdmin')));

  const history = useHistory();

  if (!isAdmin && !isCustomerService && !isStoreManager) {
    history.push('/');
    return null;
  }

  const showMessage = function (message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  };

  const filter = function () {
    if (searchText.length > 0) {
      const temp = _.filter(sides, (row) => row.name.toUpperCase()
        .includes(searchText.toUpperCase()) ||
        row.internalName.toUpperCase().includes(searchText.toUpperCase()));
      setFiltered(temp);
    } else {
      setFiltered(sides);
    }
  };

  const getSides = function () {
    setLoading(true);
    let url = 'deli/sides';
    if (!currentState.globalReqSideAddons && store) {
      url = `${store.id}/${url}`;
    }
    api
      .get(url)
      .then((response) => {
        if (response.data.success) {
          setLoading(false);
          setRefreshSides(false);
          setSides(response.data.data.results);
        } else {
          showMessage(response.data.error);
        }
      })
      .catch((error) => {
        setLoading(false);
        showMessage(error);
      });
  };

  useEffect(() => {
    getSides();
  }, []);

  useEffect(() => {
    if (refreshSides) {
      getSides();
    }
  }, [refreshSides]);

  useEffect(() => {
    if (sides) {
      filter();
    }
  }, [sides, searchText]);

  return (
    <>
      <PageHeader
        title={t('sides')}
        extra={[
          <Tag
            key="create"
            color="#1890ff"
            onClick={() => {
              setSelectedSide({});
            }}
          >
            <PlusOutlined /> {t('create')}
          </Tag>
        ]}
      />
      <Search
        placeholder={t('search_sides')}
        allowClear
        onChange={(obj) => {
          setSearchText(obj.target.value);
        }}
        value={searchText}
      />
      <Table
        size="small"
        bordered
        loading={loading}
        pagination={false}
        columns={[
          {
            title: t('internal_name'),
            dataIndex: 'internalName',
            key: 'internalName',
            align: 'center',
            className: 'text-xs',
            render: (text) => (
              <span>{text}</span>
            ),
          },
          {
            title: t('name'),
            dataIndex: 'name',
            key: 'name',
            align: 'center',
            className: 'text-xs',
            render: (text) => (
              <span>{text}</span>
            ),
          },
          {
            title: t('minimum'),
            dataIndex: 'minRequired',
            key: 'minRequired',
            align: 'center',
            className: 'text-xs',
            render: (text) => (
              <span>{text}</span>
            ),
          },
          {
            title: t('maximum'),
            dataIndex: 'maxRequired',
            key: 'maxRequired',
            align: 'center',
            className: 'text-xs',
            render: (text) => (
              <span>{text}</span>
            ),
          },
          {
            title: '',
            align: 'center',
            className: 'text-xs',
            render: (row) => (
              <Button
                size="small"
                type="primary"
                onClick={() => {
                  setSelectedSide(row);
                }}
              >
                {t('edit')}
              </Button>
            ),
          }
        ]}
        rowKey="id"
        dataSource={filtered}
        expandable={{
          expandedRowRender: (record) => (
            <Table
              size="small"
              bordered
              rowKey="id"
              dataSource={record.items}
              pagination={false}
              columns={[
                {
                  title: t('image'),
                  align: 'center',
                  className: 'text-xs',
                  render: (row) => (
                    <>
                      {row.imageUrl && row.imageUrl.length > 0 && (
                        <img
                          style={{ margin: 'auto' }}
                          width={50}
                          src={row.imageUrl}
                          alt={row.name}
                        />
                      )}
                    </>
                  ),
                },
                {
                  title: t('brand'),
                  dataIndex: 'brand',
                  key: 'brand',
                  align: 'center',
                  className: 'text-xs',
                  render: (text) => (
                    <span>{text}</span>
                  ),
                },
                {
                  title: t('name'),
                  dataIndex: 'name',
                  key: 'name',
                  align: 'center',
                  className: 'text-xs',
                  render: (text) => (
                    <span>{text}</span>
                  ),
                },
                {
                  title: t('description'),
                  dataIndex: 'description',
                  key: 'description',
                  align: 'center',
                  className: 'text-xs',
                  render: (text) => (
                    <span>{text}</span>
                  ),
                }
              ]}
            />
          ),
          rowExpandable: (record) => record.items,
        }}
      />
      {selectedSide && !selectedOption && (
        <EditItemSide />
      )}
      {selectedSide && selectedOption && (
        <EditSideOption />
      )}
    </>
  );
}

export default ItemSides;
